/**
 * ReviewPreviewPanel - Large preview panel with action buttons.
 *
 * Right panel of the review split-pane. Shows:
 * - Large image preview (click to open lightbox)
 * - Status badge
 * - Primary action buttons (Approve, Reject, Flag)
 * - Secondary actions (Include/Exclude, Set Cover)
 * - Keyboard shortcut hints
 * - Image metadata
 *
 * @see /src/features/gallery/pages/GalleryReviewPage.tsx - Parent component
 */

import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  ThumbsUp,
  ThumbsDown,
  Flag,
  ImagePlus,
  ImageMinus,
  Star,
  ZoomIn,
  Download,
  Copy,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
} from 'lucide-react'
import { GalleryStatusBadge } from '../shared/GalleryStatusBadge'
import type { GalleryAllImages } from '../../types'
import { useToast } from '@/hooks/use-toast'
import { downloadUrlAsWebP } from '@/utils/imageDownload'

interface ReviewPreviewPanelProps {
  /** Currently selected image */
  image: GalleryAllImages | null
  /** Gallery cover image URL for comparison */
  coverImageUrl?: string | null
  /** Called when approve clicked */
  onApprove: () => void
  /** Called when reject clicked */
  onReject: () => void
  /** Called when flag clicked */
  onFlag: () => void
  /** Called when include/exclude clicked */
  onToggleInclude: () => void
  /** Called when set cover clicked */
  onSetCover: () => void
  /** Called when lightbox should open */
  onOpenLightbox: () => void
  /** Navigate to previous image */
  onPrev: () => void
  /** Navigate to next image */
  onNext: () => void
  /** Whether mutations are loading */
  isLoading?: boolean
  /** Current index in list */
  currentIndex?: number
  /** Total images in filtered list */
  totalImages?: number
  /** Current filter label for counter context (e.g., "pending", "approved") */
  filterLabel?: string
  /** Additional className */
  className?: string
}

/**
 * Keyboard shortcut hint component.
 */
function ShortcutKey({ shortcut }: { shortcut: string }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 text-xs font-mono font-medium bg-muted border border-border rounded">
      {shortcut}
    </kbd>
  )
}

/**
 * Keyboard shortcuts popover - shows all available shortcuts.
 */
function KeyboardShortcutsPopover() {
  const shortcuts = [
    { key: 'A', action: 'Approve', icon: ThumbsUp, color: 'text-success' },
    { key: 'R', action: 'Reject', icon: ThumbsDown, color: 'text-destructive' },
    { key: 'F', action: 'Flag for review', icon: Flag, color: 'text-warning' },
    { key: 'I', action: 'Toggle include', icon: ImagePlus, color: 'text-muted-foreground' },
    { key: 'C', action: 'Set as cover', icon: Star, color: 'text-muted-foreground' },
    { key: '← →', action: 'Navigate images', icon: ChevronRight, color: 'text-muted-foreground' },
  ]

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground hover:text-foreground"
        >
          <HelpCircle className="h-4 w-4" />
          <span className="hidden sm:inline">Shortcuts</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3">
        <div className="space-y-1">
          <h4 className="font-medium text-sm mb-3">Keyboard Shortcuts</h4>
          {shortcuts.map(({ key, action, icon: Icon, color }) => (
            <div
              key={key}
              className="flex items-center justify-between py-1.5 text-sm"
            >
              <div className="flex items-center gap-2">
                <Icon className={cn('h-4 w-4', color)} />
                <span>{action}</span>
              </div>
              <ShortcutKey shortcut={key} />
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
          Press any key while focused on the preview panel
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Action button with shortcut hint.
 */
function ActionButton({
  icon: Icon,
  label,
  shortcut,
  onClick,
  disabled,
  variant = 'outline',
  destructive = false,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  shortcut?: string
  onClick: () => void
  disabled?: boolean
  variant?: 'default' | 'outline' | 'ghost'
  destructive?: boolean
}) {
  return (
    <Button
      variant={destructive ? 'destructive' : variant}
      size="lg"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex-1 gap-2 h-12',
        !destructive && variant === 'default' && 'bg-success hover:bg-success/90 text-success-foreground'
      )}
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
      {shortcut && <ShortcutKey shortcut={shortcut} />}
    </Button>
  )
}

export function ReviewPreviewPanel({
  image,
  coverImageUrl,
  onApprove,
  onReject,
  onFlag,
  onToggleInclude,
  onSetCover,
  onOpenLightbox,
  onPrev,
  onNext,
  isLoading = false,
  currentIndex = 0,
  totalImages = 0,
  filterLabel,
  className,
}: ReviewPreviewPanelProps) {
  const { toast } = useToast()

  const isCover = image?.image_url === coverImageUrl
  // Determine if image is included based on is_cover or sort_order (is_included doesn't exist on view)
  const isIncluded = image?.is_cover ?? image?.sort_order !== null
  const canApprove = image?.review_status !== 'approved'
  const canReject = image?.review_status !== 'rejected'

  // Copy image URL to clipboard
  const handleCopyUrl = async () => {
    if (!image?.image_url) return
    try {
      await navigator.clipboard.writeText(image.image_url)
      toast({ title: 'URL copied to clipboard' })
    } catch {
      toast({ title: 'Failed to copy URL', variant: 'destructive' })
    }
  }

  // Download as WebP
  const handleDownload = async () => {
    if (!image?.image_url) return
    try {
      await downloadUrlAsWebP(image.image_url, { filename: `image-${image.original_id}.webp` })
      toast({ title: 'Download started' })
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    }
  }

  // Empty state
  if (!image) {
    return (
      <div className={cn('flex flex-col h-full items-center justify-center p-8', className)}>
        <div className="text-center space-y-2">
          <ZoomIn className="h-12 w-12 text-muted-foreground/50 mx-auto" />
          <p className="text-muted-foreground">Select an image to preview</p>
          <p className="text-sm text-muted-foreground/70">
            Click on a thumbnail or use arrow keys to navigate
          </p>
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className={cn('flex flex-col h-full', className)}>
      {/* Image preview area */}
      <div className="relative flex-1 flex items-center justify-center p-4 bg-muted/20 min-h-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={image.original_id}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="relative max-w-full max-h-full"
          >
            {/* Main image */}
            <img
              src={image.image_url ?? ''}
              alt={image.caption ?? `Image ${image.original_id}`}
              className="max-w-full max-h-[50vh] object-contain rounded-lg shadow-lg cursor-zoom-in"
              onClick={onOpenLightbox}
            />

            {/* Status badge overlay */}
            <div className="absolute top-3 left-3">
              <GalleryStatusBadge status={image.review_status} showIcon size="md" />
            </div>

            {/* Cover badge */}
            {isCover && (
              <div className="absolute bottom-3 left-3">
                <Badge variant="default" className="gap-1">
                  <Star className="h-3 w-3 fill-current" />
                  Cover
                </Badge>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation arrows */}
        <button
          onClick={onPrev}
          disabled={totalImages <= 1}
          className={cn(
            'absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full',
            'bg-background/80 backdrop-blur shadow-sm border',
            'hover:bg-background transition-colors',
            'disabled:opacity-30 disabled:cursor-not-allowed'
          )}
          aria-label="Previous image"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={onNext}
          disabled={totalImages <= 1}
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full',
            'bg-background/80 backdrop-blur shadow-sm border',
            'hover:bg-background transition-colors',
            'disabled:opacity-30 disabled:cursor-not-allowed'
          )}
          aria-label="Next image"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Image counter with filter context */}
        {totalImages > 0 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-background/80 backdrop-blur rounded-full text-sm font-medium shadow-sm border">
            {currentIndex + 1} of {totalImages}
            {filterLabel && filterLabel !== 'all' && (
              <span className="text-muted-foreground ml-1">{filterLabel}</span>
            )}
          </div>
        )}
      </div>

      {/* Action buttons panel */}
      <div className="shrink-0 p-4 space-y-4 border-t bg-background">
        {/* Primary actions row */}
        <div className="flex gap-3">
          <ActionButton
            icon={ThumbsUp}
            label="Approve"
            shortcut="A"
            onClick={onApprove}
            disabled={isLoading || !canApprove}
            variant="default"
          />
          <ActionButton
            icon={ThumbsDown}
            label="Reject"
            shortcut="R"
            onClick={onReject}
            disabled={isLoading || !canReject}
            destructive
          />
          <ActionButton
            icon={Flag}
            label="Flag"
            shortcut="F"
            onClick={onFlag}
            disabled={isLoading}
            variant="outline"
          />
        </div>

        {/* Secondary actions row */}
        <div className="flex gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isIncluded ? 'secondary' : 'outline'}
                size="default"
                onClick={onToggleInclude}
                disabled={isLoading}
                className="flex-1 gap-2"
              >
                {isIncluded ? (
                  <>
                    <ImageMinus className="h-4 w-4" />
                    Exclude
                  </>
                ) : (
                  <>
                    <ImagePlus className="h-4 w-4" />
                    Include
                  </>
                )}
                <ShortcutKey shortcut="I" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Include in gallery export and Sanity CMS sync</p>
            </TooltipContent>
          </Tooltip>
          <Button
            variant={isCover ? 'secondary' : 'outline'}
            size="default"
            onClick={onSetCover}
            disabled={isLoading || isCover}
            className="flex-1 gap-2"
          >
            <Star className={cn('h-4 w-4', isCover && 'fill-current')} />
            {isCover ? 'Is Cover' : 'Set Cover'}
            <ShortcutKey shortcut="C" />
          </Button>
        </div>

        {/* Utility actions - grouped with visual separator */}
        <div className="flex items-center justify-between border-t pt-4 mt-4">
          <div className="flex gap-2 items-center">
            <span className="text-xs text-muted-foreground mr-2">Utilities:</span>
            <Button variant="ghost" size="sm" onClick={handleCopyUrl} className="gap-2">
              <Copy className="h-4 w-4" />
              Copy URL
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDownload} className="gap-2">
              <Download className="h-4 w-4" />
              Download
            </Button>
            {image.jobber_web_uri && (
              <Button variant="ghost" size="sm" asChild className="gap-2">
                <a href={image.jobber_web_uri} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                  Jobber
                </a>
              </Button>
            )}
          </div>

          {/* Keyboard shortcuts popover */}
          <KeyboardShortcutsPopover />
        </div>

        {/* Image metadata */}
        <div className="text-xs text-muted-foreground border-t pt-3 space-y-1">
          <div className="flex items-center gap-4 flex-wrap">
            {image.job_number && <span>Job #{image.job_number}</span>}
            {image.franchise_region && (
              <span className="capitalize">{image.franchise_region.replace('_', ' ')}</span>
            )}
            <span>ID: {image.original_id}</span>
            {image.image_url && (
              <span className="font-mono truncate max-w-[200px]" title={image.file_name ?? image.image_url.split('/').pop()}>
                {image.file_name ?? image.image_url.split('/').pop()}
              </span>
            )}
          </div>
          {image.caption && (
            <p className="text-foreground/80 truncate">{image.caption}</p>
          )}
        </div>
      </div>
    </div>
    </TooltipProvider>
  )
}
